import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  isLeadBulkActionSettled,
  leadApi,
  type LeadBulkAction,
  type LeadBulkActionKind
} from "api/lead";
import { LEAD_BULK_ACTION_LABELS } from "./labels";

// Same guards as BulkBrandingProgress: stop hammering a durably-broken status
// endpoint, and stop watching a run that never settles within a session.
const MAX_CONSECUTIVE_POLL_ERRORS = 10;
const MAX_POLL_DURATION_MS = 30 * 60 * 1000;
const POLL_INTERVAL_MS = 1000;

// The stage/assignee writes are indexed by OpenSearch asynchronously, so the refetch
// that fires on "completed" can still read a few pre-change rows. Reconcile on a short
// staggered schedule (same trick as useChangeLeadStage) so a slow reindex self-heals.
const REINDEX_RECONCILE_DELAYS_MS = [600, 1800];

const LEADS_TABLE_KEY = "LEADS_TABLE";
const LEADS_PIPELINE_COLUMN_KEY = "LEADS_PIPELINE_COLUMN";
const LEAD_ACTIVITY_LOG_KEY = "LEAD_ACTIVITY_LOG";

// sessionStorage (not localStorage): a run is a per-tab, minutes-long thing; surviving a
// reload of this tab is enough, leaking into other tabs is not wanted.
const STORAGE_KEY = "leadBulkActionProgress.run";

export type LeadBulkActionRun = {
  id: number;
  action: LeadBulkActionKind;
  // When tracking started, so MAX_POLL_DURATION_MS is measured from the run's real start
  // rather than from whenever the app remounted.
  startedAt: number;
};

const readStoredRun = (): LeadBulkActionRun | null => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const isValid =
      typeof parsed?.id === "number" &&
      typeof parsed?.action === "string" &&
      typeof parsed?.startedAt === "number";
    return isValid ? parsed : null;
  } catch {
    return null;
  }
};

const writeStoredRun = (run: LeadBulkActionRun | null) => {
  try {
    if (run) window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(run));
    else window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable — tracking just won't survive a reload.
  }
};

const pluralLeads = (count: number) => (count === 1 ? "lead" : "leads");

type LeadBulkActionProgressContextValue = {
  run: LeadBulkActionRun | null;
  status: LeadBulkAction | undefined;
  isStatusError: boolean;
  hasGivenUpPolling: boolean;
  /** True while a tracked run is known (or assumed) to be in flight. */
  isRunning: boolean;
  startTracking: (run: LeadBulkAction) => void;
  dismiss: () => void;
};

const LeadBulkActionProgressContext =
  createContext<LeadBulkActionProgressContextValue>({
    run: null,
    status: undefined,
    isStatusError: false,
    hasGivenUpPolling: false,
    isRunning: false,
    startTracking: () => {},
    dismiss: () => {}
  });

/**
 * Tracks one filter-wide leads bulk action (CRM-1449) independently of the Leads page:
 * the toolbar only starts the run and hands the id here, so progress survives the
 * modal closing and the user navigating elsewhere. LeadBulkActionProgressPanel renders it.
 */
export function LeadBulkActionProgressProvider({
  children
}: {
  children: ReactNode;
}) {
  const queryClient = useQueryClient();
  const [run, setRun] = useState<LeadBulkActionRun | null>(readStoredRun);
  const settledRunIdRef = useRef<number | null>(null);
  // A run resumed from storage that turns out to be already settled was toasted about
  // before the reload; only re-toast for runs actually observed in flight.
  const hasObservedUnsettledRef = useRef(false);

  const { data: status, isError: isStatusError } = useQuery<LeadBulkAction>({
    queryKey: ["LEAD_BULK_ACTION", run?.id],
    queryFn: () => leadApi.getBulkAction(run!.id),
    enabled: run !== null,
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      if (s && isLeadBulkActionSettled(s)) return false;
      if (query.state.fetchFailureCount >= MAX_CONSECUTIVE_POLL_ERRORS)
        return false;
      if (run && Date.now() - run.startedAt > MAX_POLL_DURATION_MS)
        return false;
      return POLL_INTERVAL_MS;
    }
  });

  const hasGivenUpPolling = Boolean(
    run &&
    status &&
    !isLeadBulkActionSettled(status.status) &&
    Date.now() - run.startedAt > MAX_POLL_DURATION_MS
  );

  const isRunning = Boolean(
    run &&
    !isStatusError &&
    !hasGivenUpPolling &&
    (!status || !isLeadBulkActionSettled(status.status))
  );

  useEffect(() => {
    if (!status) return;
    if (!isLeadBulkActionSettled(status.status)) {
      hasObservedUnsettledRef.current = true;
      return;
    }
    if (settledRunIdRef.current === status.id) return;
    settledRunIdRef.current = status.id;

    const reconcile = () => {
      queryClient.refetchQueries({ queryKey: [LEADS_TABLE_KEY] });
      queryClient.invalidateQueries({ queryKey: [LEADS_PIPELINE_COLUMN_KEY] });
      queryClient.refetchQueries({ queryKey: [LEAD_ACTIVITY_LOG_KEY] });
    };
    reconcile();
    REINDEX_RECONCILE_DELAYS_MS.forEach((delay) =>
      setTimeout(reconcile, delay)
    );

    if (!hasObservedUnsettledRef.current) return;

    const labels = LEAD_BULK_ACTION_LABELS[status.action];
    const total = status.total_count ?? 0;
    const succeeded = Math.max(0, status.processed_count - status.failed_count);
    if (status.status === "failed")
      toast.error(
        status.error
          ? `Bulk action failed: ${status.error}`
          : "Bulk action failed before it could finish"
      );
    else if (status.failed_count > 0)
      toast.warning(
        `${succeeded.toLocaleString()} of ${total.toLocaleString()} ${pluralLeads(total)} ${labels.done}, ${status.failed_count.toLocaleString()} failed`
      );
    else
      toast.success(
        `${succeeded.toLocaleString()} ${pluralLeads(succeeded)} ${labels.done}`
      );
  }, [status, queryClient]);

  const startTracking = useCallback((next: LeadBulkAction) => {
    const withStartedAt: LeadBulkActionRun = {
      id: next.id,
      action: next.action,
      startedAt: Date.now()
    };
    settledRunIdRef.current = null;
    hasObservedUnsettledRef.current = true;
    writeStoredRun(withStartedAt);
    setRun(withStartedAt);
  }, []);

  const dismiss = useCallback(() => {
    writeStoredRun(null);
    setRun(null);
  }, []);

  const value = useMemo(
    () => ({
      run,
      status,
      isStatusError,
      hasGivenUpPolling,
      isRunning,
      startTracking,
      dismiss
    }),
    [
      run,
      status,
      isStatusError,
      hasGivenUpPolling,
      isRunning,
      startTracking,
      dismiss
    ]
  );

  return (
    <LeadBulkActionProgressContext.Provider value={value}>
      {children}
    </LeadBulkActionProgressContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useLeadBulkActionProgress() {
  return useContext(LeadBulkActionProgressContext);
}
